import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { usePriceFreezeOfferQuery } from '@/hooks/GQL';
import { useHideWaitingPage } from '@/hooks/useHideWaitingPage';
import { NOT_AVAILABLE } from '@/components/ErrorBoundary';
import { useLogin } from '@/providers/login/LoginProvider';
import { useIsSubscriptionOffersLoading } from '@/providers/SubscriptionsProvider';

const PriceFreezeOfferContext = createContext(null);

export function PriceFreezeOfferProvider({
  searchId,
  fareItineraryKey,
  segmentKeys,
  skeleton,
  children,
}) {
  const { isLoading: isUserLoading } = useLogin();
  const isSubscriptionLoading = useIsSubscriptionOffersLoading();
  const {
    data,
    errors,
    loading: isOfferLoading,
  } = usePriceFreezeOfferQuery({
    searchId,
    fareItineraryKey,
    segmentKeys,
  });

  const overallLoading =
    isOfferLoading || isUserLoading || isSubscriptionLoading;

  useHideWaitingPage(overallLoading, 'price_freeze_waiting_page');

  if (overallLoading) {
    return skeleton || '';
  }

  if (errors) {
    throw new Error(JSON.stringify(errors));
  }

  if (data.itineraryPriceFreezeOffer == null) {
    throw new Error(NOT_AVAILABLE);
  }

  return (
    <PriceFreezeOfferContext.Provider value={data.itineraryPriceFreezeOffer}>
      {children}
    </PriceFreezeOfferContext.Provider>
  );
}

PriceFreezeOfferProvider.propTypes = {
  searchId: PropTypes.string.isRequired,
  fareItineraryKey: PropTypes.string.isRequired,
  segmentKeys: PropTypes.arrayOf(PropTypes.number).isRequired,
  skeleton: PropTypes.element,
};

export function usePriceFreezeOffer() {
  return useContext(PriceFreezeOfferContext);
}

export function useItinerarySelector(selector) {
  const priceFreezeOffer = useContext(PriceFreezeOfferContext);

  return selector(priceFreezeOffer.itinerary);
}

export function useSearchCriteriaSelector(selector) {
  const priceFreezeOffer = useContext(PriceFreezeOfferContext);

  return selector(priceFreezeOffer.searchCriteria);
}

export function useOffer() {
  const priceFreezeOffer = useContext(PriceFreezeOfferContext);

  return priceFreezeOffer?.offer;
}

export function useIsAvailableOffer() {
  const priceFreezeOffer = useContext(PriceFreezeOfferContext);
  return priceFreezeOffer?.available;
}

export function PriceFreezeOfferEntrypointProvider({ offerData, children }) {
  return (
    <PriceFreezeOfferContext.Provider
      value={offerData?.itineraryPriceFreezeOffer}
    >
      {children}
    </PriceFreezeOfferContext.Provider>
  );
}
